import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache del prefijo del país (compartido entre instancias).
let cachedPaisPrefijo = null;
let cachedPromise = null;
async function fetchPaisPrefijo() {
  if (cachedPaisPrefijo !== null) return cachedPaisPrefijo;
  if (cachedPromise) return cachedPromise;
  cachedPromise = fetch(`${API_URL}/api/public/config/telefono`)
    .then((r) => (r.ok ? r.json() : { pais_prefijo: '34' }))
    .then((d) => {
      cachedPaisPrefijo = (d.pais_prefijo || '34').replace(/\D/g, '') || '34';
      return cachedPaisPrefijo;
    })
    .catch(() => {
      cachedPaisPrefijo = '34';
      return '34';
    });
  return cachedPromise;
}

/**
 * Permite limpiar la caché desde el admin tras guardar un prefijo nuevo.
 */
export function invalidatePaisPrefijoCache() {
  cachedPaisPrefijo = null;
  cachedPromise = null;
}

/**
 * Input de teléfono con modo nacional / internacional.
 *
 * Props:
 *  - value: string canónico (sólo dígitos). National = 9 dígitos. Internacional = prefijo+numero.
 *  - onChange(canonical: string): callback con el valor canónico.
 *  - label?: string
 *  - placeholder?: string
 *  - testIdSuffix?: string (sufijo opcional para los data-testid)
 *  - required?: boolean
 *  - id?: string
 *  - className?: string
 *  - autoComplete?: string
 */
export default function PhoneInput({
  value = '',
  onChange,
  label = 'Teléfono',
  placeholder = '6XXXXXXXX',
  testIdSuffix = '',
  required = false,
  id,
  className,
  autoComplete = 'tel',
  readOnly = false,
  disabled = false,
}) {
  const [defaultPrefix, setDefaultPrefix] = useState('34');
  const [intl, setIntl] = useState(false);
  const [prefix, setPrefix] = useState('');
  const [number, setNumber] = useState('');
  // Track the last canonical value we emitted, para no re-parsear nuestros propios cambios.
  const lastValueRef = useRef(undefined);

  // Cargar prefijo del país una vez
  useEffect(() => {
    fetchPaisPrefijo().then((pp) => setDefaultPrefix(pp));
  }, []);

  // Re-parsear sólo cuando `value` cambie desde FUERA (no por nuestro propio emit)
  useEffect(() => {
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    const v = (value || '').replace(/\D/g, '');
    const pp = defaultPrefix;
    if (!v) {
      setIntl(false); setPrefix(''); setNumber('');
    } else if (v.length <= 9) {
      setIntl(false); setPrefix(''); setNumber(v);
    } else if (pp && v.startsWith(pp) && v.length - pp.length === 9) {
      // Caso típico: vino con el prefijo del país pero longitud local. Lo guardamos
      // como nacional para la UI pero permitimos al usuario marcar intl manualmente
      // (entonces lastValueRef bloqueará el re-parseo).
      setIntl(false); setPrefix(''); setNumber(v.slice(pp.length));
    } else {
      setIntl(true);
      const commons = ['351', '49', '44', '39', '34', '33', '7', '1', '52', '54', '55', '56', '57', '58']
        .sort((a, b) => b.length - a.length);
      let pfx = '', num = v;
      for (const c of commons) {
        if (v.startsWith(c)) { pfx = c; num = v.slice(c.length); break; }
      }
      setPrefix(pfx); setNumber(num);
    }
  }, [value, defaultPrefix]);

  const emit = (newIntl, newPrefix, newNumber) => {
    let canonical;
    if (newIntl) {
      canonical = (newPrefix || '').replace(/\D/g, '') + (newNumber || '').replace(/\D/g, '');
    } else {
      canonical = (newNumber || '').replace(/\D/g, '').slice(0, 9);
    }
    lastValueRef.current = canonical;
    onChange && onChange(canonical);
  };

  const onCheckChange = (e) => {
    const checked = e.target.checked;
    setIntl(checked);
    if (!checked) {
      // pasar de intl → nacional: prefix se pierde, sólo conservamos los últimos 9 dígitos
      const digits = (number || '').replace(/\D/g, '');
      const trimmed = digits.slice(-9);
      setPrefix('');
      setNumber(trimmed);
      emit(false, '', trimmed);
    } else {
      // pasar de nacional → intl: precargar el prefijo por defecto
      const p = defaultPrefix || '';
      setPrefix(p);
      emit(true, p, number);
    }
  };

  const onPrefixChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPrefix(v);
    emit(intl, v, number);
  };

  const onNumberChange = (e) => {
    // Permitir espacios al teclear (más cómodo en copy/paste), pero los limpiamos al emit
    let raw = e.target.value;
    // En internacional admitimos hasta 20 dígitos; en nacional, 9.
    const digits = raw.replace(/\D/g, '');
    const trimmed = intl ? digits.slice(0, 20) : digits.slice(0, 9);
    setNumber(trimmed);
    emit(intl, prefix, trimmed);
  };

  const inputId = id || `phone-${testIdSuffix || 'input'}`;

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={inputId} className="text-sm">
          {label}
          {required && ' *'}
        </Label>
      )}
      <div className="mt-1 flex items-stretch gap-2">
        {intl && (
          <div className="flex items-stretch" data-testid={`phone-prefix-wrap${testIdSuffix ? '-' + testIdSuffix : ''}`}>
            <span className="inline-flex items-center px-2 rounded-l border border-r-0 bg-gray-100 text-sm font-medium text-gray-700 select-none">+</span>
            <Input
              type="tel"
              inputMode="numeric"
              value={prefix}
              onChange={onPrefixChange}
              placeholder="34"
              maxLength={4}
              className="w-16 rounded-l-none"
              aria-label="Prefijo país"
              data-testid={`phone-prefix${testIdSuffix ? '-' + testIdSuffix : ''}`}
            />
          </div>
        )}
        <Input
          id={inputId}
          type="tel"
          inputMode="numeric"
          value={number}
          onChange={onNumberChange}
          placeholder={intl ? 'Tu número sin prefijo' : placeholder}
          maxLength={intl ? 20 : 9}
          autoComplete={autoComplete}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          className="flex-1"
          data-testid={`phone-number${testIdSuffix ? '-' + testIdSuffix : ''}`}
        />
      </div>
      <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer" data-testid={`phone-intl-toggle${testIdSuffix ? '-' + testIdSuffix : ''}`}>
        <input
          type="checkbox"
          checked={intl}
          onChange={onCheckChange}
          className="rounded"
          aria-label="Internacional"
        />
        <span>Internacional (hasta 20 dígitos)</span>
      </label>
    </div>
  );
}
